import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { 
  BookOpen, CheckCircle, Clock, Award, TrendingUp, 
  PlayCircle, FileText, LogOut, ChevronRight, Calendar, UserCheck,
  MessageSquare, Trophy, Star, Zap, Medal
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StudentDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, loading: authLoading } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [attendance, setAttendance] = useState(null);
  const [gamification, setGamification] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/student/dashboard`, { withCredentials: true });
      setDashboard(res.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAttendance = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/student/attendance`, { withCredentials: true });
      setAttendance(res.data);
    } catch (error) {
      console.error('Failed to load attendance');
    }
  }, []);

  const fetchGamification = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/student/gamification`, { withCredentials: true });
      setGamification(res.data);
    } catch (error) {
      console.error('Failed to load gamification');
    }
  }, []);

  const recordLogin = useCallback(async () => {
    try {
      const res = await axios.post(`${API}/student/record-login`, {}, { withCredentials: true });
      if (res.data.points_awarded > 0 && res.data.streak > 1) {
        toast.success(`+${res.data.points_awarded} points! ${res.data.streak} day streak!`);
      }
    } catch (error) {
      // Login recording is non-critical - log for debugging but don't disrupt user
      console.debug('Login recording failed:', error.message);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/student/login');
    } else if (user && user.role !== 'student') {
      navigate('/');
    } else if (user) {
      fetchDashboard();
      fetchAttendance();
      fetchGamification();
      recordLogin();
    }
  }, [user, authLoading, navigate, fetchDashboard, fetchAttendance, fetchGamification, recordLogin]);

  const handleLogout = async () => {
    await logout();
    navigate('/student/login');
  };

  if (authLoading || loading) {
    return (
      <div className="pt-20 min-h-screen bg-[#060F22] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00AEEF]"></div>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen bg-[#060F22]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white">Welcome back, {user?.name?.split(' ')[0]}!</h1>
            <p className="text-[#94A3B8]">Continue your learning journey</p>
          </div>
          <button
            onClick={handleLogout}
            data-testid="student-logout-btn"
            className="flex items-center gap-2 bg-[#0A1F44] text-[#94A3B8] hover:text-white px-4 py-2 rounded-xl border border-[#00AEEF]/20 hover:border-[#FF6A00] transition-all"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>

        {/* Progress Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-[#00AEEF]/20 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-[#00AEEF]" />
              </div>
              <div>
                <p className="text-3xl font-bold text-white">{dashboard?.stats?.completion_percentage || 0}%</p>
                <p className="text-sm text-[#94A3B8]">Overall Progress</p>
              </div>
            </div>
          </div>
          <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-[#00FFE0]/20 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-[#00FFE0]" />
              </div>
              <div>
                <p className="text-3xl font-bold text-white">{dashboard?.stats?.completed_lessons || 0}</p>
                <p className="text-sm text-[#94A3B8]">Lessons Completed</p>
              </div>
            </div>
          </div>
          <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-[#FF6A00]/20 flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-[#FF6A00]" />
              </div>
              <div>
                <p className="text-3xl font-bold text-white">{dashboard?.stats?.total_lessons || 0}</p>
                <p className="text-sm text-[#94A3B8]">Total Lessons</p>
              </div>
            </div>
          </div>
          <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                <Award className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <p className="text-3xl font-bold text-white">{dashboard?.assignments?.length || 0}</p>
                <p className="text-sm text-[#94A3B8]">Assignments Done</p>
              </div>
            </div>
          </div>
        </div>

        {/* Attendance Card */}
        {attendance && (
          <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Calendar className="w-6 h-6 text-[#00AEEF]" />
                <h2 className="text-xl font-bold text-white">Attendance</h2>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                attendance.stats.attendance_rate >= 80 ? 'bg-green-500/20 text-green-400' :
                attendance.stats.attendance_rate >= 60 ? 'bg-yellow-500/20 text-yellow-400' :
                'bg-red-500/20 text-red-400'
              }`}>
                {attendance.stats.attendance_rate}% Attendance Rate
              </span>
            </div>
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-[#060F22]/50 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-white">{attendance.stats.total_days}</p>
                <p className="text-xs text-[#94A3B8]">Total Days</p>
              </div>
              <div className="bg-[#060F22]/50 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-[#00FFE0]">{attendance.stats.present}</p>
                <p className="text-xs text-[#94A3B8]">Present</p>
              </div>
              <div className="bg-[#060F22]/50 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-yellow-400">{attendance.stats.late}</p>
                <p className="text-xs text-[#94A3B8]">Late</p>
              </div>
              <div className="bg-[#060F22]/50 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-red-400">{attendance.stats.absent}</p>
                <p className="text-xs text-[#94A3B8]">Absent</p>
              </div>
            </div>
            {attendance.records.length > 0 && (
              <div className="mt-4 pt-4 border-t border-[#00AEEF]/10">
                <p className="text-sm text-[#94A3B8] mb-2">Recent Attendance:</p>
                <div className="flex flex-wrap gap-2">
                  {attendance.records.slice(0, 7).map((r, i) => (
                    <div 
                      key={i}
                      className={`px-3 py-1 rounded-lg text-xs font-medium ${
                        r.status === 'present' ? 'bg-green-500/20 text-green-400' :
                        r.status === 'late' ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-red-500/20 text-red-400'
                      }`}
                    >
                      {new Date(r.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Gamification Card */}
        {gamification && (
          <Link 
            to="/student/achievements"
            data-testid="gamification-card"
            className="block bg-gradient-to-r from-[#0A1F44] to-[#0A1F44]/50 border border-[#FFD700]/30 rounded-xl p-6 mb-8 hover:border-[#FFD700]/50 transition-all group"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-[#FFD700]/20 flex items-center justify-center">
                  <Star className="w-6 h-6 text-[#FFD700]" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Level {gamification.level.level}: {gamification.level.name}</h2>
                  <p className="text-sm text-[#94A3B8]">{gamification.total_points.toLocaleString()} total points</p>
                </div>
              </div>
              <ChevronRight className="w-6 h-6 text-[#94A3B8] group-hover:text-[#FFD700] transition-colors" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-[#060F22]/50 rounded-xl p-3 text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <Zap className="w-4 h-4 text-[#FF6A00]" />
                  <p className="text-xl font-bold text-white">{gamification.total_points}</p>
                </div>
                <p className="text-xs text-[#94A3B8]">Points</p>
              </div>
              <div className="bg-[#060F22]/50 rounded-xl p-3 text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <Medal className="w-4 h-4 text-[#FFD700]" />
                  <p className="text-xl font-bold text-white">{gamification.badges_earned}</p>
                </div>
                <p className="text-xs text-[#94A3B8]">Badges</p>
              </div>
              <div className="bg-[#060F22]/50 rounded-xl p-3 text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <TrendingUp className="w-4 h-4 text-[#00FFE0]" />
                  <p className="text-xl font-bold text-white">{gamification.level_progress}%</p>
                </div>
                <p className="text-xs text-[#94A3B8]">To Next Level</p>
              </div>
            </div>
            {gamification.points_to_next_level > 0 && (
              <div className="mt-4">
                <div className="w-full bg-[#060F22] rounded-full h-2">
                  <div 
                    className="h-2 rounded-full transition-all duration-500"
                    style={{ 
                      width: `${gamification.level_progress}%`,
                      backgroundColor: gamification.level.color 
                    }}
                  ></div>
                </div>
                <p className="text-xs text-[#94A3B8] mt-2 text-center">
                  {gamification.points_to_next_level} points to reach next level
                </p>
              </div>
            )}
          </Link>
        )}

        {/* Progress Bar */}
        <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-white">Course Progress</h2>
            <span className="text-[#00FFE0] font-bold">{dashboard?.stats?.completion_percentage || 0}%</span>
          </div>
          <div className="w-full bg-[#060F22] rounded-full h-4">
            <div 
              className="bg-gradient-to-r from-[#00AEEF] to-[#00FFE0] h-4 rounded-full transition-all duration-500"
              style={{ width: `${dashboard?.stats?.completion_percentage || 0}%` }}
            ></div>
          </div>
        </div>

        {/* Quick Links */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Link
            to="/student/courses"
            data-testid="nav-courses"
            className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-6 hover:border-[#00FFE0]/50 transition-all group"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-[#00AEEF]/20 flex items-center justify-center">
                  <PlayCircle className="w-7 h-7 text-[#00AEEF]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">My Courses</h3>
                  <p className="text-[#94A3B8]">Continue learning</p>
                </div>
              </div>
              <ChevronRight className="w-6 h-6 text-[#94A3B8] group-hover:text-[#00FFE0] transition-colors" />
            </div>
          </Link>

          <Link
            to="/student/assignments"
            data-testid="nav-assignments"
            className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-6 hover:border-[#00FFE0]/50 transition-all group"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-[#FF6A00]/20 flex items-center justify-center">
                  <FileText className="w-7 h-7 text-[#FF6A00]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Assignments</h3>
                  <p className="text-[#94A3B8]">View submissions</p>
                </div>
              </div>
              <ChevronRight className="w-6 h-6 text-[#94A3B8] group-hover:text-[#00FFE0] transition-colors" />
            </div>
          </Link>

          <Link
            to="/student/messages"
            data-testid="nav-messages"
            className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-6 hover:border-[#00FFE0]/50 transition-all group"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-green-500/20 flex items-center justify-center">
                  <MessageSquare className="w-7 h-7 text-green-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Messages</h3>
                  <p className="text-[#94A3B8]">Teacher messages</p>
                </div>
              </div>
              <ChevronRight className="w-6 h-6 text-[#94A3B8] group-hover:text-[#00FFE0] transition-colors" />
            </div>
          </Link>
        </div>

        {/* Second Row Links */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Link
            to="/student/achievements"
            data-testid="nav-achievements"
            className="bg-gradient-to-br from-[#FFD700]/10 to-[#FF6A00]/5 border border-[#FFD700]/30 rounded-xl p-6 hover:border-[#FFD700]/50 transition-all group"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-[#FFD700]/20 flex items-center justify-center">
                  <Star className="w-7 h-7 text-[#FFD700]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Achievements</h3>
                  <p className="text-[#94A3B8]">Points & badges</p>
                </div>
              </div>
              <ChevronRight className="w-6 h-6 text-[#94A3B8] group-hover:text-[#FFD700] transition-colors" />
            </div>
          </Link>

          <Link
            to="/student/certificates"
            data-testid="nav-certificates"
            className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-6 hover:border-[#00FFE0]/50 transition-all group"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-yellow-500/20 flex items-center justify-center">
                  <Trophy className="w-7 h-7 text-yellow-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Certificates</h3>
                  <p className="text-[#94A3B8]">Your achievements</p>
                </div>
              </div>
              <ChevronRight className="w-6 h-6 text-[#94A3B8] group-hover:text-[#00FFE0] transition-colors" />
            </div>
          </Link>

          <Link
            to="/student/resources"
            data-testid="nav-resources"
            className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-6 hover:border-[#00FFE0]/50 transition-all group"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-purple-500/20 flex items-center justify-center">
                  <BookOpen className="w-7 h-7 text-purple-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Resources</h3>
                  <p className="text-[#94A3B8]">Learning materials</p>
                </div>
              </div>
              <ChevronRight className="w-6 h-6 text-[#94A3B8] group-hover:text-[#00FFE0] transition-colors" />
            </div>
          </Link>

          <Link
            to="/student/support"
            data-testid="nav-support"
            className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-6 hover:border-[#00FFE0]/50 transition-all group"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                  <Award className="w-7 h-7 text-cyan-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Support</h3>
                  <p className="text-[#94A3B8]">Get help</p>
                </div>
              </div>
              <ChevronRight className="w-6 h-6 text-[#94A3B8] group-hover:text-[#00FFE0] transition-colors" />
            </div>
          </Link>
        </div>

        {/* Recent Activity */}
        <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-6">
          <h2 className="text-xl font-bold text-white mb-4">Recent Activity</h2>
          {dashboard?.progress?.length > 0 ? (
            <div className="space-y-3">
              {dashboard.progress.slice(0, 5).map((p, i) => (
                <div key={i} className="flex items-center gap-3 p-3 bg-[#060F22]/50 rounded-xl">
                  <CheckCircle className="w-5 h-5 text-[#00FFE0]" />
                  <span className="text-[#94A3B8]">Completed a lesson</span>
                  <span className="text-[#94A3B8] text-sm ml-auto">
                    {new Date(p.completed_at).toLocaleDateString()}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[#94A3B8]">No activity yet. Start a lesson to begin!</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentDashboard;
